import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, Download, Loader2, X } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { chaveNotaBaixaSchema, MAX_NOTA_BAIXA_BYTES, podeDarBaixaLaudo } from '@shared/laudo-baixa';
import type { Laudo } from '@shared/schema';

type Props = { laudo: Pick<Laudo, 'id' | 'laudoId' | 'parceiroId' | 'status' | 'baixa' | 'baixaEm' | 'baixaChaveNota' | 'baixaNotaNome'>; onSaved(): Promise<void> };

export function BaixaLaudo({ laudo, onSaved }: Props) {
  const { profile, session } = useAuth();
  const { toast } = useToast();
  const [aberto, setAberto] = useState(false);
  const [chave, setChave] = useState('');
  const [nota, setNota] = useState<File>();
  const arquivoInput = useRef<HTMLInputElement>(null);
  const [erro, setErro] = useState('');
  const [baixando, setBaixando] = useState(false);
  const podeBaixar = !!profile && podeDarBaixaLaudo({ ...profile, empresaId: profile.empresa?.id }, laudo);
  const arquivoInvalido = nota && (nota.size === 0 || nota.size > MAX_NOTA_BAIXA_BYTES || !/\.(pdf|xml|jpe?g|png)$/i.test(nota.name));

  const confirmar = useMutation({
    mutationFn: async () => {
      const parsed = chaveNotaBaixaSchema.safeParse(chave);
      if (!parsed.success) throw new Error(parsed.error.issues[0].message);
      if (!session?.access_token) throw new Error('Sua sessão expirou. Entre novamente para dar baixa.');
      const headers: Record<string, string> = { Authorization: `Bearer ${session.access_token}` };
      let body: BodyInit;
      if (nota) {
        headers['Content-Type'] = nota.type || (/\.xml$/i.test(nota.name) ? 'application/xml' : /\.pdf$/i.test(nota.name) ? 'application/pdf' : /\.png$/i.test(nota.name) ? 'image/png' : 'image/jpeg');
        headers['X-Nota-Nome'] = encodeURIComponent(nota.name);
        if (parsed.data) headers['X-Nota-Chave'] = parsed.data;
        body = nota;
      } else {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify({ chaveNota: parsed.data });
      }
      let response: Response;
      try { response = await fetch(`/api/laudos/${laudo.id}/baixa`, { method: 'POST', headers, body, signal: AbortSignal.timeout(45_000) }); }
      catch { throw new Error('Não foi possível confirmar o resultado. Atualize o laudo antes de tentar novamente.'); }
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        if (response.status === 409) await onSaved();
        throw new Error(data.message || 'Não foi possível registrar a baixa. Tente novamente.');
      }
    },
    onSuccess: async () => {
      setAberto(false); setChave(''); setNota(undefined); setErro('');
      await onSaved();
      toast({ title: 'Baixa registrada com sucesso.' });
    },
    onError: (error: Error) => setErro(error.message),
  });

  async function baixarNota() {
    setBaixando(true);
    try {
      const response = await fetch(`/api/laudos/${laudo.id}/baixa/nota`, {
        headers: { Authorization: `Bearer ${session?.access_token}` }, signal: AbortSignal.timeout(30_000),
      });
      if (!response.ok) throw new Error('Não foi possível baixar a nota. Tente novamente.');
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement('a'); link.href = url; link.download = laudo.baixaNotaNome ?? 'nota-fiscal'; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) { toast({ title: 'Nota indisponível', description: (error as Error).message, variant: 'destructive' }); }
    finally { setBaixando(false); }
  }

  if (laudo.status !== 'finalizado' && !laudo.baixa) return null;
  return <>
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <h2 className="text-xl font-bold">Baixa do laudo</h2>
        <Badge variant={laudo.baixa ? 'default' : 'secondary'}>{laudo.baixa ? 'Baixa registrada' : 'Sem baixa'}</Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        {laudo.baixa ? <>
          <p className="text-sm text-muted-foreground">{laudo.baixaEm ? `Registrada em ${new Date(laudo.baixaEm).toLocaleString('pt-BR')}.` : 'Baixa registrada pelo parceiro.'}</p>
          {laudo.baixaChaveNota && <div><p className="text-sm font-medium">Chave da nota fiscal</p><p className="mt-1 break-all text-sm">{laudo.baixaChaveNota}</p></div>}
          {laudo.baixaNotaNome && <Button variant="outline" onClick={() => void baixarNota()} disabled={baixando} className="h-auto max-w-full whitespace-normal py-2">
            {baixando ? <Loader2 className="mr-2 size-4 shrink-0 animate-spin" /> : <Download className="mr-2 size-4 shrink-0" />}<span className="min-w-0 break-all">Baixar nota fiscal: {laudo.baixaNotaNome}</span>
          </Button>}
          {!laudo.baixaChaveNota && !laudo.baixaNotaNome && <p className="text-sm text-muted-foreground">Registrada sem nota fiscal.</p>}
        </> : podeBaixar ? <>
          <p className="text-sm text-muted-foreground">Registre a baixa deste laudo finalizado. A nota fiscal é opcional.</p>
          <Button onClick={() => { setErro(''); setAberto(true); }}><CheckCircle2 className="mr-2 size-4" />Dar baixa</Button>
        </> : <p className="text-sm text-muted-foreground">Aguardando baixa pelo parceiro.</p>}
      </CardContent>
    </Card>
    <Dialog open={aberto} onOpenChange={(value) => { if (!confirmar.isPending) setAberto(value); }}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Dar baixa no laudo</DialogTitle>
          <DialogDescription>Confirme a baixa do laudo {laudo.laudoId ?? laudo.id}. Você pode informar a chave, anexar a nota fiscal ou continuar sem esses dados.</DialogDescription>
        </DialogHeader>
        <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); if (!confirmar.isPending && !arquivoInvalido) confirmar.mutate(); }}>
          <div className="space-y-2">
            <Label htmlFor="baixa-chave-nota">Chave de acesso da NF-e (opcional)</Label>
            <Input id="baixa-chave-nota" inputMode="numeric" value={chave} maxLength={60} onChange={(e) => { setChave(e.target.value); setErro(''); }} disabled={confirmar.isPending} aria-describedby="baixa-chave-ajuda" />
            <p id="baixa-chave-ajuda" className="text-sm text-muted-foreground">Informe os 44 dígitos da chave, se tiver.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="baixa-nota">Anexar nota fiscal (opcional)</Label>
            <input id="baixa-nota" ref={arquivoInput} className="sr-only" tabIndex={-1} type="file" accept=".pdf,.xml,.jpg,.jpeg,.png" disabled={confirmar.isPending}
              onChange={(e) => { setNota(e.target.files?.[0]); setErro(''); }} aria-describedby="baixa-nota-ajuda" />
            <Button type="button" variant="outline" disabled={confirmar.isPending} onClick={() => arquivoInput.current?.click()}>{nota ? 'Trocar arquivo' : 'Selecionar arquivo'}</Button>
            <p id="baixa-nota-ajuda" className="text-sm text-muted-foreground">PDF, XML de NF-e, JPG ou PNG, até 5 MB.</p>
            {nota && <div className="flex min-w-0 items-start gap-2"><span className="min-w-0 flex-1 break-all text-sm">{nota.name}</span>
              <Button type="button" variant="ghost" size="icon" aria-label="Remover nota selecionada" disabled={confirmar.isPending} onClick={() => { setNota(undefined); setErro(''); if (arquivoInput.current) arquivoInput.current.value = ''; }}><X className="size-4" /></Button></div>}
          </div>
          {(erro || arquivoInvalido) && <p role="alert" className="text-sm text-destructive">{arquivoInvalido ? 'Selecione um PDF, XML, JPG ou PNG válido de até 5 MB.' : erro}</p>}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" disabled={confirmar.isPending} onClick={() => setAberto(false)}>Cancelar</Button>
            <Button type="submit" disabled={confirmar.isPending || !!arquivoInvalido}>{confirmar.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}{confirmar.isPending ? 'Registrando baixa…' : 'Confirmar baixa'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  </>;
}
